import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, money } from "@/lib/api";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { HardDrives, Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const DEFAULT_SERVER_TYPES = [
  { value: "media", label: "Media Server" },
  { value: "ai_engine", label: "AI Engine Server" },
  { value: "pd", label: "PD Server" },
];
const DEFAULT_TYPES = [
  { value: "shared", label: "Shared" },
  { value: "dedicated", label: "Dedicated" },
];

const EMPTY = { name: "", server_type: "media", type: "shared", call_limit: "", price: "" };

function toMinor(v) {
  if (v === "" || v == null) return null;
  const n = parseFloat(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function toDollars(m) {
  if (m == null) return "";
  return (Number(m) / 100).toFixed(2);
}

export default function ServerPricing() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || can(user, "servers-pricing", "edit");
  const [items, setItems] = useState([]);
  const [serverTypes, setServerTypes] = useState(DEFAULT_SERVER_TYPES);
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/servers/pricing");
      setItems(data.items || []);
      if (Array.isArray(data.server_types) && data.server_types.length) {
        setServerTypes(data.server_types);
      }
      if (Array.isArray(data.types) && data.types.length) setTypes(data.types);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEdit(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEdit(row);
    setForm({
      name: row.name || "",
      server_type: row.server_type || "media",
      type: row.type || "shared",
      call_limit: row.call_limit != null ? String(row.call_limit) : "",
      price: toDollars(row.price_minor),
    });
    setOpen(true);
  };

  const save = async () => {
    const name = (form.name || "").trim();
    const callLimit = parseInt(form.call_limit, 10);
    const priceMinor = toMinor(form.price);
    if (!name) { toast.error("Plan name is required"); return; }
    if (!form.server_type) { toast.error("Server type is required"); return; }
    if (!form.type) { toast.error("Type is required"); return; }
    if (!Number.isFinite(callLimit) || callLimit < 1) {
      toast.error("Enter a valid call limit (1 or more)"); return;
    }
    if (priceMinor == null) { toast.error("Enter a valid price"); return; }

    setSaving(true);
    try {
      const body = {
        name,
        server_type: form.server_type,
        type: form.type,
        call_limit: callLimit,
        price_minor: priceMinor,
      };
      if (edit) {
        await api.patch(`/servers/pricing/${edit.id}`, body);
        toast.success("Plan updated");
      } else {
        await api.post("/servers/pricing", body);
        toast.success("Plan added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete plan “${row.name}”?`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/servers/pricing/${row.id}`);
      toast.success("Plan deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="server-pricing-page">
      <PageHeader
        title="Server Pricing"
        subtitle="Named plans by server kind (Media, AI Engine, PD), Shared or Dedicated, and price."
      >
        {canEdit && (
          <Button
            type="button"
            className="rounded-lg"
            onClick={openAdd}
            data-testid="server-pricing-add"
          >
            <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add plan
          </Button>
        )}
      </PageHeader>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          testid="server-pricing-empty"
          icon={HardDrives}
          title="No server pricing plans"
          description="Add a plan with name, server type, Shared/Dedicated, and price."
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan name</TableHead>
                <TableHead>Server type</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Call limit</TableHead>
                <TableHead>Price</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} data-testid={`server-plan-row-${row.id}`}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell>{row.server_type_label || row.server_type}</TableCell>
                  <TableCell>{row.type_label || row.type}</TableCell>
                  <TableCell className="tabular-nums">{row.call_limit ?? "—"}</TableCell>
                  <TableCell className="tabular-nums font-medium">
                    {money(row.price_minor)}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="rounded-lg h-8 w-8 p-0"
                          onClick={() => openEdit(row)}
                          data-testid={`server-plan-edit-${row.id}`}
                          title="Edit"
                        >
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="rounded-lg h-8 w-8 p-0 text-destructive hover:text-destructive"
                          disabled={busyId === row.id}
                          onClick={() => remove(row)}
                          data-testid={`server-plan-delete-${row.id}`}
                          title="Delete"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="server-plan-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? "Edit plan" : "Add plan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="sp-name">Plan name</Label>
              <Input
                id="sp-name"
                data-testid="sp-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Media Shared Starter"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Server type</Label>
              <Select
                value={form.server_type}
                onValueChange={(v) => setForm((f) => ({ ...f, server_type: v }))}
              >
                <SelectTrigger data-testid="sp-server-type">
                  <SelectValue placeholder="Select server type" />
                </SelectTrigger>
                <SelectContent>
                  {serverTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
              >
                <SelectTrigger data-testid="sp-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sp-call-limit">Call limit</Label>
              <Input
                id="sp-call-limit"
                data-testid="sp-call-limit"
                type="number"
                min="1"
                step="1"
                value={form.call_limit}
                onChange={(e) => setForm((f) => ({ ...f, call_limit: e.target.value }))}
                placeholder="e.g. 100"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sp-price">Price (USD)</Label>
              <div className="relative max-w-[220px]">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  id="sp-price"
                  data-testid="sp-price"
                  type="number"
                  min="0"
                  step="0.01"
                  className="pl-7"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-lg"
              disabled={saving}
              onClick={save}
              data-testid="sp-save"
            >
              {saving ? "Saving…" : edit ? "Save changes" : "Add plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
